import { zodResolver } from '@hookform/resolvers/zod';
import { FormEvent, useEffect, useId, useReducer, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  H1,
  H2,
  Input,
  Select,
  Option,
  Table,
  TableBody,
  TableData,
  TableHead,
  TableRow,
} from 'ui-rollup';
import { z } from 'zod';
import { User } from '../../types/user';
import { fetchData } from '../../utils/fetchData';
import { getJwt } from '../../utils/getJwt';
import styles from './create.module.css';

type State = {
  client: {
    id: string;
    name: string;
  };
  dateOfIssue: string;
  items: Item[];
};

// TODO: Duplicate type. Reuse schema
type Item = {
  id: string;
  quantity: string;
  description: string;
  rate: number;
  gst: number;
  isIgst: boolean;
  discount: number;
  amount: string;
};

type Action =
  | { type: 'setClient'; payload: State['client'] }
  | { type: 'addItem'; payload: Item }
  | { type: 'removeItem'; payload: string }
  | {
      type: 'setDateOfIssue';
      payload: State['dateOfIssue'];
    };

const initialState: State = {
  client: {
    id: '',
    name: '',
  },
  dateOfIssue: '',
  items: [],
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'setClient':
      return {
        ...state,
        client: action.payload,
      };
    case 'addItem':
      return {
        ...state,
        items: [...state.items, action.payload],
      };
    case 'removeItem':
      const items = state.items.filter((item) => {
        return item.id !== action.payload;
      });
      return {
        ...state,
        items,
      };
    case 'setDateOfIssue':
      return {
        ...state,
        dateOfIssue: action.payload,
      };
    default:
      throw new Error('Unsupported action type');
  }
}

let itemIndex = 0;

export const Create = () => {
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(reducer, initialState);
  const [clients, setClients] = useState<User[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const id = useId();
  const user = getJwt();

  useEffect(() => {
    fetchData<User[]>(
      `${process.env.REACT_APP_BACKEND_API}/users/${user.id}/clients`,
    ).then((data) => setClients(data));
  }, [user.id]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    const data = await fetchData<any>(
      `${process.env.REACT_APP_BACKEND_API}/bills/`,
      {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem('token')}`,
        },
        method: 'POST',
        body: JSON.stringify({
          userId: user.id,
          dateOfIssue: new Date(state.dateOfIssue).toISOString(),
          clientId: state.client.id,
          products: state.items.map((item) => ({
            ...item,
            rate: Number(item.rate),
            gst: Number(item.gst),
            amount: Number(item.amount),
          })),
        }),
      },
    )
      .catch((error) => console.error('Error while creating bill', error))
      .finally(() => {
        setIsSubmitting(false);
      });

    if (data.id) {
      navigate(`/history/${data.id}`);
    }
  };

  const calculateTotal = state.items.reduce(
    (acc, item) => acc + parseFloat(item.amount),
    0,
  );

  return (
    <>
      <H1>
        Create Invoice <span>🧾</span>
      </H1>
      <form onSubmit={handleSubmit}>
        <div className={styles.inputGroup}>
          <Input
            name="client"
            list="client-list"
            label="Search client name"
            className={styles.client}
            onChange={(e) => {
              const client = clients.find(
                (client) => client.gst === e.target.value,
              );
              dispatch({
                type: 'setClient',
                payload: {
                  id: client?.id ?? '',
                  name: client?.name ?? e.target.value,
                },
              });
            }}
            value={state.client.name}
            required
          />
          <DataList id="client-list" list={clients} />
          <Input
            type="date"
            name="dateOfIssue"
            label="Date of issue"
            value={state.dateOfIssue}
            className={styles.dateOfIssue}
            onChange={(e) => {
              dispatch({
                type: 'setDateOfIssue',
                payload: e.target.value,
              });
            }}
            required
          />
        </div>
        <Table>
          <TableRow>
            <TableHead>Description</TableHead>
            <TableHead>Quantity</TableHead>
            <TableHead>Rate</TableHead>
            <TableHead>GST</TableHead>
            <TableHead>IGST</TableHead>
            <TableHead>Amount</TableHead>
          </TableRow>
          <TableBody>
            <TableRow>
              <AddItemComponent
                submitHandler={(data) =>
                  dispatch({
                    type: 'addItem',
                    payload: {
                      id: `${id}-${itemIndex++}`,
                      ...data,
                    },
                  })
                }
              />
            </TableRow>
            {state.items.map((item) => (
              <TableRow key={item.description}>
                <TableData>
                  <Input
                    name="description"
                    type="text"
                    value={item.description}
                  />
                </TableData>
                <TableData>
                  <Input name="quantity" type="number" value={item.quantity} />
                </TableData>
                <TableData>
                  <Input name="rate" type="number" value={item.rate} />
                </TableData>
                <TableData>
                  <Select name="gst" value={item.gst}>
                    <Option>0</Option>
                    <Option>5</Option>
                    <Option>12</Option>
                    <Option>18</Option>
                    <Option>28</Option>
                  </Select>
                </TableData>
                <TableData>
                  <Input
                    name="isIgst"
                    type="checkbox"
                    defaultChecked={item.isIgst}
                  />
                </TableData>
                <TableData>
                  <Input name="amount" type="number" value={item.amount} />
                </TableData>
                <TableData>
                  <Button
                    onClick={() =>
                      dispatch({
                        type: 'removeItem',
                        payload: item.id,
                      })
                    }
                  >
                    🗑️
                  </Button>
                </TableData>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div className={styles.footerContainer}>
          <H2>
            Total: <strong>₹ {calculateTotal}</strong>
          </H2>
        </div>
        <Button
          disabled={state.items.length === 0 || isSubmitting}
          type="submit"
        >
          {isSubmitting ? 'Creating . . .' : 'Create'}
        </Button>
      </form>
    </>
  );
};

const DataList = ({ id, list }: { id: string; list: User[] }) => {
  // TODO: Replace this with React-Select or implement from scratch
  return (
    <datalist id={id}>
      {list.map((item) => (
        <option
          key={item.id}
          value={item.gst}
          label={`${item.name} - ${item.gst}`}
        />
      ))}
    </datalist>
  );
};

const AddItemComponent = ({ submitHandler }) => {
  const schema = z.object({
    description: z.string().min(3),
    quantity: z.number().min(1),
    rate: z.number().nonnegative(),
    gst: z.number().default(0),
    isIgst: z.boolean().default(false),
    discount: z.number().default(0), //Add this in form as well
  });
  type AddItem = z.infer<typeof schema>;

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<AddItem>({
    resolver: zodResolver(schema),
  });

  const [quantity, gst, rate] = useWatch({
    name: ['quantity', 'gst', 'rate'],
    control,
  });

  const amount = calculateAmount(quantity, rate, gst);

  return (
    <>
      <TableData className={styles.tableDataDescription}>
        <Input
          autoComplete="false"
          {...register('description', {
            required: true,
          })}
          errorMessage={errors.description?.message}
        />
      </TableData>
      <TableData>
        <Input
          type="number"
          autoComplete="false"
          {...register('quantity', { required: true, valueAsNumber: true })}
          errorMessage={errors.quantity?.message}
        />
      </TableData>
      <TableData className={styles.tableDataRate}>
        <Input
          type="number"
          autoComplete="false"
          {...register('rate', { required: true, valueAsNumber: true })}
          errorMessage={errors.rate?.message}
        />
      </TableData>
      <TableData className={styles.tableDataGst}>
        <Select {...register('gst', { required: true, valueAsNumber: true })}>
          {[0, 5, 12, 18, 28].map((gst) => (
            <Option value={gst} key={gst}>
              {gst}
            </Option>
          ))}
        </Select>
      </TableData>
      <TableData>
        <Input
          type="checkbox"
          {...register('isIgst')}
          errorMessage={errors.isIgst?.message}
        />
      </TableData>
      <TableData className={styles.tableDataAmount}>
        <Input type="number" disabled value={amount} />
      </TableData>
      <TableData>
        <Button
          onClick={handleSubmit((data) => {
            submitHandler({
              ...data,
              amount,
            });
            reset();
          })}
        >
          ➕
        </Button>
      </TableData>
    </>
  );
};

const calculateAmount = (quantity: number, rate: number, gst: number) => {
  const total = quantity * rate;

  return Number.parseFloat(String(total + (total * Number(gst)) / 100)).toFixed(
    2,
  );
};
